import React, { useEffect, useRef, useMemo, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { Chart, registerables } from 'chart.js';
import './TimeGraph.css';

Chart.register(...registerables);

const formatTime = (milliseconds) => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const totalHours = Math.floor(totalSeconds / 3600);
    const remainingSecondsAfterHours = totalSeconds % 3600;
    const minutes = Math.floor(remainingSecondsAfterHours / 60);
    const seconds = remainingSecondsAfterHours % 60;
    return `${totalHours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

const DISTANCE_TOLERANCE = 0.05;

const calculateElevationGain = (data, minThreshold = 2, maxThreshold = 100) => {
    let gain = 0;
    for (let i = 1; i < data.length; i++) {
        const delta = data[i].elevation - data[i - 1].elevation;
        if (delta > minThreshold && delta < maxThreshold) {
            gain += delta;
        }
    }
    return Math.round(gain);
};

const formatRestTime = (minutes) => {
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${h}時間${m}分`;
};

const TimeGraph = ({
    results, 
    pointsData, 
    appliedManualPoints, 
    elapsedTime,
    raceLimitTime,   // ★ FINISH時刻
    limit,   // ★ 制限時間
    restTime,   // ★ 休憩時時間
}) => {
    const chartRef = useRef(null);
    const [highlightPointsData, setHighlightPointsData] = useState([]);
    const [totalTime, setTotalTime] = useState('');
    const [totalElevationGain, setTotalElevationGain] = useState(0);

    const finishPoint = useMemo(() => pointsData.find(p => p.区分 === 'FINISH'), [pointsData]);
    const finishResult = useMemo(() => results[pointsData.findIndex(p => p.区分 === 'FINISH')], [results, pointsData]);

    const snappedManualPoints = useMemo(() => {
        return appliedManualPoints.map(mp => {
            const rawDistance = parseFloat(mp.distance);
            if (isNaN(rawDistance)) return mp;
            const closest = pointsData.reduce((acc, p) => {
                const diff = Math.abs(p.cumulative_distance - rawDistance);
                return diff < acc.diff ? { point: p, diff } : acc;
            }, { point: null, diff: Infinity });
            return { ...mp, distance: closest.point ? closest.point.cumulative_distance.toFixed(2) : mp.distance };
        });
    }, [appliedManualPoints, pointsData]);

    useEffect(() => {
        const newHighlightPointsData = [];
        snappedManualPoints.forEach(manualPoint => {
            const manualDistance = parseFloat(manualPoint.distance);
            if (!isNaN(manualDistance)) {
                let closestIndex = -1;
                let minDifference = Infinity;
                pointsData.forEach((point, index) => {
                    const difference = Math.abs(point.cumulative_distance - manualDistance);
                    if (difference < minDifference) {
                        minDifference = difference;
                        closestIndex = index;
                    }
                });
                const matchedDistance = pointsData[closestIndex]?.cumulative_distance;
                if (closestIndex !== -1 && results[closestIndex] && Math.abs(matchedDistance - manualDistance) < 0.2) {
                    newHighlightPointsData.push({
                        x: matchedDistance,
                        y: results[closestIndex].elevation,
                        label: manualPoint.name || 'ステーション',
                        gateTime: manualPoint.gateTime,
                    });
                }
            }
        });
        setHighlightPointsData(newHighlightPointsData);
    }, [appliedManualPoints, results, pointsData]);

    useEffect(() => {
        setTotalTime(elapsedTime !== undefined ? formatTime(elapsedTime) : '');
        setTotalElevationGain(calculateElevationGain(results, 0, 10));
    }, [results, elapsedTime]);

    // ★ グラデーション生成（赤ライン → 緑の地形）
    const gradientFill = useMemo(() => {
        const chart = chartRef.current;
        if (!chart) return null;

        const ctx = chart.ctx;
        const { top, bottom } = chart.chartArea;

        const gradient = ctx.createLinearGradient(0, top, 0, bottom);

        // ★ 上：濃い緑（ライン付近）
        gradient.addColorStop(0, 'rgba(42, 207, 42, 0.61)');

        // ★ 中間：少し薄く
        gradient.addColorStop(0.5, 'rgba(62, 206, 62, 0.43)');

        // ★ 下：ほぼ透明
        gradient.addColorStop(1, 'rgba(62, 206, 62, 0.05)');

        return gradient;
    }, [chartRef.current, results]);



    const chartData = useMemo(() => {
        return {
            labels: results.map((_, index) => pointsData[index]?.cumulative_distance),
            datasets: [
                {
                    label: '標高',
                    data: results.map((result, index) => ({
                        x: pointsData[index]?.cumulative_distance,
                        y: result?.elevation
                    })),
                    borderColor: 'rgb(4, 4, 4)',   // 赤ライン
                    backgroundColor: gradientFill || 'rgba(62, 206, 62, 0.38)', // ★ グラデーション
                    fill: true,
                    yAxisID: 'y',
                    pointRadius: 0,
                    hoverRadius: 0,
                    pointHoverRadius: 0,
                    hitRadius: 0,
                    borderWidth: 1,
                    tension: 0.3,
                    order: 1,
                },

                // ★ エイド
                ...highlightPointsData.length > 0 ? [{
                    label: 'エイド',
                    data: highlightPointsData.map(p => ({ x: p.x, y: p.y })),
                    pointRadius: 6,
                    hitRadius: 20,
                    showLine: false,
                    yAxisID: 'y',
                    order: 0,
                    pointBackgroundColor: (context) =>
                        highlightPointsData[context.dataIndex].gateTime ? 'red' : 'rgba(0, 0, 255, 0.7)',
                    pointBorderColor: (context) =>
                        highlightPointsData[context.dataIndex].gateTime ? 'red' : 'rgba(0, 0, 255, 0.7)',
                }] : [],

                // ★ FINISH（◇）※ borderColor: 'blue' を維持
                ...finishPoint && finishResult ? [{
                    label: 'FINISH',
                    data: [{ x: finishPoint.cumulative_distance, y: finishResult.elevation }],
                    borderColor: 'blue',     // ← 最新仕様を維持
                    backgroundColor: 'gold',
                    pointRadius: 8,
                    hitRadius: 20,
                    pointStyle: 'rectRot',
                    pointBorderWidth: 2,
                    showLine: false,
                    yAxisID: 'y',
                    order: 0,
                }] : []
            ],
        };
    }, [results, pointsData, highlightPointsData, finishPoint, finishResult]);

    const chartOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        aspectRatio: 5,
        interaction: { mode: 'nearest', intersect: true },
        scales: {
            x: { title: { display: true, text: '累積距離 (km)' }, type: 'linear' },
            y: {
                title: { display: true, text: '標高 (m)' },
                beginAtZero: false,

                // ★ tick を 500m 固定にする
                ticks: {
                    stepSize: 500,
                    padding: 8,
                },

                // ★ min/max を 500 の倍数に丸める（これが超重要）
                afterDataLimits: (scale) => {
                    scale.min = Math.floor(scale.min / 500) * 500;
                    scale.max = Math.ceil(scale.max / 500) * 500;
                },

                position: 'left',
                grid: {
                    color: (context) => {
                        const value = context.tick.value;

                        if (value % 1000 === 0) {
                            return '#444';               // ★ 1000m は濃い線
                        }
                        if (value % 500 === 0) {
                            return '#888';               // ★ 500m は中くらい
                        }
                        return 'rgba(0,0,0,0.08)';        // ★ その他は薄く
                    },

                    lineWidth: (context) => {
                        const value = context.tick.value;

                        if (value % 1000 === 0) {
                            return 1.0;                  // ★ 1000m は太線
                        }
                        if (value % 500 === 0) {
                            return 0.5;                  // ★ 500m は中くらい
                        }
                        return 0.4;                      // ★ その他は細線
                    },

                    borderDash: (context) => {
                        const value = context.tick.value;

                        if (value % 1000 === 0) {
                            return [];                   // ★ 1000m は実線
                        }
                        if (value % 500 === 0) {
                            return [];                   // ★ 500m も実線
                        }
                        return [4, 4];                   // ★ その他は点線（任意）
                    }
                }
            },
            y2: {
                title: { display: true, text: '標高 (m)' },
                position: 'right',
                grid: { drawOnChartArea: false },
                ticks: {
                    stepSize: 50,
                    padding: 10,
                    callback: (value) => value
                },
                afterUpdate: (scale) => {
                    const y = scale.chart.scales.y;
                    scale.min = y.min;
                    scale.max = y.max;
                    scale.ticks = y.ticks.map(t => ({ ...t }));
                }
            },
        },

        plugins: {
            legend: { display: false },
            tooltip: {
                filter: (item) => item.dataset.label === 'エイド' || item.dataset.label === 'FINISH',
                // R2.2.3 Apple風デザインを追加
                backgroundColor: 'rgba(255, 255, 255, 0.85)', // 白半透明
                titleColor: '#000',
                bodyColor: '#000',
                borderColor: 'rgba(0,0,0,0.15)',
                borderWidth: 1,
                padding: 10,
                cornerRadius: 8,
                shadowOffsetX: 0,
                shadowOffsetY: 4,
                shadowBlur: 12,
                shadowColor: 'rgba(0, 0, 0, 0.25)',    
                // R2.2.3 Apple風デザインを追加
                callbacks: {
                    label: (context) => {
                        const label = context.dataset.label;
                        const x = context.parsed.x;
                        const resultIndex = pointsData.findIndex(p => Math.abs(p.cumulative_distance - x) < DISTANCE_TOLERANCE);
                        const resultData = results[resultIndex];

                        if (label === 'エイド') {
                            const manualPoint = snappedManualPoints.find(mp => Math.abs(parseFloat(mp.distance) - x) < DISTANCE_TOLERANCE);

                            const lines = [
                                `${manualPoint.type}${manualPoint.name ? `(${manualPoint.name})` : ''}`,
                                `累積距離: ${x.toFixed(1)} km`,
                                `標高: ${resultData.elevation.toFixed(1)} m`,
                                `到着時刻: ${resultData.arrivalTime.slice(0, 5)}`
                            ];

                            if (manualPoint.gateTime) {
                                lines.push(`関門時間: ${manualPoint.gateTime}`);
                            }

                            return lines;
                        }

                        if (label === 'FINISH') {
                            const lines = [
                                'FINISH地点',
                                `累積距離: ${x.toFixed(1)} km`,
                                `標高: ${resultData.elevation.toFixed(1)} m`,
                                `到着時刻: ${resultData.arrivalTime.slice(0, 5)}`
                            ];

                            // ★ 制限時間を追加
                            if (raceLimitTime) {
                                lines.push(`制限時間: ${raceLimitTime}`);
                            }

                            return lines;
                        }

                        return null;
                    },
                    title: () => '',
                },
            },
        },

    }), [results, pointsData, snappedManualPoints, raceLimitTime]);

    // ★ グラフの横幅を累積距離に応じて動的に変更
    const totalDistance = pointsData[pointsData.length - 1]?.cumulative_distance || 0;
    const dynamicWidth = Math.max(600, totalDistance * 15); 
    
    return (
        <div>
            <h2 style={{
                fontSize: '1.5rem',
                fontWeight: 800,
                color: '#4a5568',
                textAlign: 'center',
                marginBottom: '12px'
                }}>
                シミュレーション結果
            </h2>

            {/* ★ スマホ対応のため CSS クラスに変更 */}
            <div className="simulation-summary">
                {elapsedTime !== undefined && (
                    <p>
                        総所要時間： {totalTime}
                    </p>
                )}

                {limit && (
                    <p className="limit-time">
                        制限時間： {limit}
                    </p>
                )}

                {totalElevationGain > 0 && (
                    <p>
                        累積標高 (+D)：{totalElevationGain.toFixed(0)} m
                    </p>
                )}
            </div>

            <div className="graph-container">
                <div
                className="graph-content"
                style={{ position: 'relative', height: '400px', minWidth: `${dynamicWidth}px` }}
                >
                    <Line data={chartData} options={chartOptions} ref={chartRef} />
                </div>
            </div>
        </div>

    );
};

export default TimeGraph;
